import React from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

const inputCls = "w-full border border-parchment rounded-md px-3 py-2.5 text-ink bg-white focus:outline-none focus:border-forest";

const EMPTY = {
  title: "", subtitle: "", price: 0, level: "Beginner", lectures: "", duration: "",
  learn: [], banner_image: "", accent: "#C0562B", description: "", published: true,
};

const LEVELS = ["Beginner", "Intermediate", "Advanced", "All levels"];

// Inline course create/edit popup used on the public Courses page for admins.
export default function CourseEditorModal({ course, onClose, onSaved }) {
  const isNew = course === "new";
  const [form, setForm] = React.useState(isNew ? EMPTY : { ...EMPTY, ...course });
  const [learnText, setLearnText] = React.useState(isNew ? "" : (course.learn || []).join("\n"));
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [uploadPct, setUploadPct] = React.useState(null);

  const uploadBanner = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      setUploadPct(0);
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => { if (ev.total) setUploadPct(Math.min(99, Math.round((ev.loaded / ev.total) * 100))); },
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, banner_image: url }));
      toast.success("Banner uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally { setUploading(false); setUploadPct(null); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Course title is required."); return; }
    setSaving(true);
    const payload = {
      title: form.title, subtitle: form.subtitle, price: Number(form.price) || 0,
      level: form.level || "Beginner", lectures: form.lectures, duration: form.duration,
      learn: learnText.split("\n").map((l) => l.trim()).filter(Boolean),
      banner_image: form.banner_image, accent: form.accent || "#C0562B",
      description: form.description, published: form.published,
    };
    try {
      if (isNew) { await api.post("/admin/courses", payload); toast.success("Course added."); }
      else { await api.put(`/admin/courses/${course.id}`, payload); toast.success("Course updated."); }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="course-editor">
      <div className="absolute inset-0 bg-night/60" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-cream h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-cream border-b border-parchment px-6 py-4 flex items-center justify-between z-10">
          <h3 className="font-serif text-2xl text-ink">{isNew ? "New Course" : "Edit Course"}</h3>
          <button onClick={onClose} className="grid place-items-center w-10 h-10 rounded-lg hover:bg-sand"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-ink mb-1.5">Course title</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="course-title-input" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1.5">Subtitle / package line</label>
            <input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder="Starter Course for Beginners (Lessons 1–5)" data-testid="course-subtitle-input" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Price (USD)</label>
              <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="course-price-input" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Level</label>
              <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} data-testid="course-level-input" className={inputCls}>
                {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Lectures</label>
              <input value={form.lectures} onChange={(e) => setForm({ ...form, lectures: e.target.value })} placeholder="10 lectures" data-testid="course-lectures-input" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Duration</label>
              <input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="5-7 weeks" data-testid="course-duration-input" className={inputCls} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1.5">Things you'll learn <span className="text-ink-muted font-normal">(one per line)</span></label>
            <textarea value={learnText} onChange={(e) => setLearnText(e.target.value)} rows={5} placeholder="Alphabet&#10;Numbers&#10;Greetings & everyday conversations" data-testid="course-learn-input" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1.5">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="course-description-input" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1.5">Banner image (the patterned strip)</label>
            <label className="inline-flex items-center gap-2 bg-forest text-cream text-sm font-semibold px-4 py-2 rounded-full hover:bg-forest-light transition-colors cursor-pointer">
              {uploading ? "Uploading…" : "Upload banner"}
              <input type="file" accept="image/*" onChange={uploadBanner} disabled={uploading} data-testid="course-banner-upload-input" className="hidden" />
            </label>
            <input value={form.banner_image} onChange={(e) => setForm({ ...form, banner_image: e.target.value })} placeholder="…or paste an image URL" data-testid="course-banner-url-input" className={`${inputCls} mt-2`} />
            {uploadPct !== null && (
              <div className="mt-3">
                <div className="flex justify-between text-xs text-ink-muted mb-1"><span>Uploading…</span><span>{uploadPct}%</span></div>
                <div className="h-2 rounded-full bg-parchment overflow-hidden"><div className="h-full bg-gold transition-all duration-200" style={{ width: `${uploadPct}%` }} /></div>
              </div>
            )}
            {form.banner_image && <img src={form.banner_image} alt="banner" className="w-full h-28 object-cover rounded-lg border border-parchment mt-3" />}
          </div>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="course-published-checkbox" className="w-4 h-4 accent-forest" />
            Published
          </label>
          <button type="submit" disabled={saving} data-testid="save-course-button" className="w-full bg-forest text-cream font-semibold py-3.5 rounded-md hover:bg-forest-light transition-colors disabled:opacity-60">
            {saving ? "Saving..." : "Save Course"}
          </button>
        </form>
      </div>
    </div>
  );
}
